// src/context/PreloadContext.jsx
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { shuffleArray } from '../utils/helpers';

// Provide a safe default so consumers can destructure properties without
// guarding against a null context. This prevents `Cannot read properties of null`
// when a component tries to access `preloadedData` before the provider mounted.
const PreloadContext = createContext({
    preloadedData: null,
    journeyData: null,
    loading: true,
    journeyLoading: false,
    preloadJourneyActivities: () => {},
    clearJourneyCache: () => {},
    prepareActivityData: () => null,
});

export const usePreloadedData = () => useContext(PreloadContext);

export const PreloadProvider = ({ children }) => {
    const { user } = useAuth();

    // Separate global preload (app-wide) from journey-specific preload
    const [preloadedData, setPreloadedData] = useState(null);
    const [journeyData, setJourneyData] = useState(null); // NEW: Journey-specific cache
    const [loading, setLoading] = useState(true);
    const [journeyLoading, setJourneyLoading] = useState(false); // NEW: Journey load state
    const [hasPreloaded, setHasPreloaded] = useState(false);
    // Global preload - runs once on app mount
    // This is fine for standalone games and general data
    const preloadAllData = useCallback(async (backendApiUrl) => {
        if (hasPreloaded) return; // ✅ prevent reruns
        setHasPreloaded(true);
        if (!backendApiUrl) {
            console.error("Backend API URL is not available for preloading.");
            setLoading(false);
            return;
        }

        try {
            console.log('🌍 Global preload: Fetching app-wide data...');

            // Fixed key mapping to match what your hooks expect
            const endpoints = [
                { url: '/quiz', key: 'quiz' },
                { url: '/halalvsharam', key: 'halalvsharam' },
                { url: '/ayah-blanks', key: 'fillintheblanks' },
                { url: '/figurematchgame/questions', key: 'figurematch' },
            ];

            const authHeaders = user && user.token
                ? { Authorization: `Bearer ${user.token}` }
                : {};

            const requests = endpoints.map(({ url }) =>
                axios.get(`${backendApiUrl}${url}`, { headers: authHeaders })
                    .catch(error => {
                        console.error(`Failed to preload ${url}:`, error.message);
                        return { data: null };
                    })
            );

            const responses = await Promise.all(requests);

            //Map to correct keys that match your hooks
            const dataMap = {};
            endpoints.forEach(({ key }, index) => {
                dataMap[key] = responses[index]?.data;
            });

            setPreloadedData(dataMap);

            console.log("✅ Global preload complete!", {
                quiz: dataMap.quiz?.length || 0,
                halalvsharam: dataMap.halalvsharam?.length || 0,
                fillintheblanks: dataMap.fillintheblanks?.length || 0,
                figurematch: dataMap.figurematch?.length || 0,
            });
        } catch (error) {
            console.error("❌ Unexpected error during global preload:", error);
        } finally {
            setLoading(false);
        }
    }, [user, hasPreloaded]);

    // ✅ NEW: Journey-specific preload
    // Called when user selects time preference in JourneyPage
    // This preloads ONLY the activities in that specific journey
    const preloadJourneyActivities = useCallback(async (lessonPlan, backendApiUrl) => {
        if (!lessonPlan || !backendApiUrl) {
            console.warn("⚠️ Cannot preload journey: missing lesson plan or API URL");
            return;
        }

        setJourneyLoading(true);
        console.log('🗺️ Journey preload: Fetching data for', lessonPlan.length, 'activities...');

        try {
            const authHeaders = user && user.token
                ? { Authorization: `Bearer ${user.token}` }
                : {};

            // Fetch only the activities in THIS journey
            const requests = lessonPlan.map(async (activity, index) => {
                try {
                    let url;

                    switch(activity.type) {
                        case 'quiz':
                            url = '/quiz';
                            break;
                        case 'halalvsharam':
                            url = '/halalvsharam';
                            break;
                        case 'fillintheblanks':
                            url = '/ayah-blanks';
                            break;
                        case 'flashcards':
                            // Fetch specific surah for flashcards
                            url = `/flashcards/surah/${activity.config.surah}`;
                            break;
                        case 'figurematch':
                            url = '/figurematchgame/questions';
                            break;
                        default:
                            console.warn(`Unknown activity type: ${activity.type}`);
                            return { activityIndex: index, data: null };
                    }

                    console.log(`  📦 [${index + 1}/${lessonPlan.length}] Fetching ${activity.type}...`);
                    const response = await axios.get(`${backendApiUrl}${url}`, { headers: authHeaders });

                    return {
                        activityIndex: index,
                        activityType: activity.type,
                        data: response.data
                    };
                } catch (error) {
                    console.error(`Failed to preload activity ${index} (${activity.type}):`, error.message);
                    return { activityIndex: index, activityType: activity.type, data: null };
                }
            });

            const results = await Promise.all(requests);

            // Cache data by activity index for instant access
            const journeyCache = {};
            results.forEach(result => {
                if (result && result.data) {
                    journeyCache[result.activityIndex] = {
                        type: result.activityType,
                        data: result.data,
                        timestamp: Date.now()
                    };
                }
            });

            setJourneyData(journeyCache);

            console.log("✅ Journey preload complete!", {
                activities: Object.keys(journeyCache).length,
                details: Object.entries(journeyCache).map(([idx, cache]) =>
                    `${idx}: ${cache.type} (${cache.data?.length || 0} items)`
                )
            });

        } catch (error) {
            console.error("❌ Journey preload failed:", error);
        } finally {
            setJourneyLoading(false);
        }
    }, [user]);

    // Clear journey cache when user leaves journey
    const clearJourneyCache = useCallback(() => {
        console.log('🧹 Clearing journey cache...');
        setJourneyData(null);
    }, []);

    // PREPARE ACTIVITY DATA (NEW)
// - Purpose: create a "prepared" shuffled copy of the activity's data the first time
// a user presses "Start Activity". This prevents a visible 'shuffle' flicker on mount
    const prepareActivityData = useCallback((activityIndex) => {
        if (!journeyData) {
            console.warn(`⚠️ prepareActivityData: no journey cache available (index ${activityIndex})`);
            return null;
        }

        const entry = journeyData[activityIndex];
        if (!entry) {
            console.warn(`⚠️ prepareActivityData: no entry for activity index ${activityIndex}`);
            return null;
        }

// If we already prepared (shuffled) this activity for the session, return it
        if (entry.prepared && Array.isArray(entry.preparedData)) {
            return entry.preparedData;
        }

// Ensure we have an array to shuffle
        const sourceArray = Array.isArray(entry.data) ? entry.data : [];

// Shuffle a shallow copy of the data (using your existing helper)
        const shuffled = shuffleArray(sourceArray);

// Cache the prepared copy inside the journeyData so subsequent calls return the
// same order for the remainder of this journey session.
        setJourneyData(prev => {
            if (!prev) return prev; // defensive
            const existing = prev[activityIndex];
            if (!existing) return prev; // defensive
// If another concurrent call prepared it first, don't overwrite
            if (existing.prepared && Array.isArray(existing.preparedData)) return prev;

            return {
                ...prev,
                [activityIndex]: {
                    ...existing,
                    prepared: true,
                    preparedData: shuffled
                }
            };
        });

// Return the freshly prepared data.
        return shuffled;
    }, [journeyData, setJourneyData]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL;
        if (!preloadedData) {
            preloadAllData(backendApiUrl);
        }
    }, [preloadAllData, preloadedData]); // run once

    // Provide both global and journey-specific data
    return (
        <PreloadContext.Provider value={{
            preloadedData,           // Global data for standalone games
            journeyData,             // Journey-specific cached data
            loading,                 // Global loading state
            journeyLoading,          // Journey preload loading state
            preloadJourneyActivities, // Function to preload journey
            clearJourneyCache, // Function to clear journey cache
            prepareActivityData
        }}>
            {children}
        </PreloadContext.Provider>
    );
};